import { AssessmentScaleGroupAlias } from 'store/interfaces/ScorecardExternalSubgoalRO';

export enum Sentiment {
  Positive = 'positive',
  Neutral = 'neutral',
  Negative = 'negative',
}

/**
 * Normalizes a single reviewer's scale pick to a percentage anchored at
 * `100 = meets expectations`, so sentiment is comparable across scales.
 *
 * 0–10 scales (NPS, 10-grade) cannot be distinguished from fractional/percentage
 * scales by magnitude, so they are routed by the scale `alias`:
 *   - NPS:      0–6 detractor (<100), 7–8 passive (100), 9–10 promoter (>100).
 *   - 10-grade: backend `TEN_GRADE_SCORE_MAPPING_DATA` buckets (80/90/100/115).
 *   - Standard (fractional `0.8`–`1.15`) → ×100; General/percentage left as-is.
 *
 * Returns `null` for missing picks, and for the legacy `0` "N/A" / "Don't know"
 * option on non-0–10 scales (where `0` is not a real grade).
 */
export const scaleValueToScore = (
  value: number | null | undefined,
  alias?: string,
): number | null => {
  if (value === null || value === undefined) return null;
  if (alias === AssessmentScaleGroupAlias.NPS) {
    if (value >= 9) return 115;
    if (value >= 7) return 100;
    return 80;
  }
  if (alias === AssessmentScaleGroupAlias.TEN_GRADE) {
    if (value >= 9) return 115;
    if (value >= 7) return 100;
    if (value >= 5) return 90;
    return 80;
  }
  if (value === 0) return null;
  return value > 10 ? value : value * 100;
};

/**
 * Sentiment for an aggregated total score (subgoal `totalScore`,
 * goal-level computed score, etc.). Totals are aggregated percentages
 * where `100` = "on target", matching the General/Standard scale's
 * neutral point.
 */
export const sentimentForScore = (
  value: number | null | undefined,
): Sentiment | null => {
  if (value === null || value === undefined) return null;
  if (value > 100) return Sentiment.Positive;
  if (value < 100) return Sentiment.Negative;
  return Sentiment.Neutral;
};

/**
 * Same as `sentimentForScore` but returns `'empty'` for unknown values
 * — convenient for components that key styles on the four-state union
 * (`positive | neutral | negative | empty`).
 */
export const sentimentForScoreOrEmpty = (
  value: number | null | undefined,
): Sentiment | 'empty' => sentimentForScore(value) ?? 'empty';

/**
 * Sentiment for an individual reviewer's scale pick, normalized per scale via
 * `scaleValueToScore` so 0–10 scales (NPS, 10-grade) classify correctly.
 */
export const sentimentForScaleValue = (
  value: number | null | undefined,
  alias?: string,
): Sentiment | null => sentimentForScore(scaleValueToScore(value, alias));

export const sentimentForScale = (
  scale: { value?: number | null; alias?: string } | null | undefined,
  alias?: string,
): Sentiment | null =>
  sentimentForScaleValue(scale?.value, alias ?? scale?.alias);
